import React from 'react';
import PropTypes from 'prop-types';
import { Eye, EyeOff } from 'lucide-react';
import './ClientViewedBadge.css';

function formatRelativeTime(isoString) {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';

  const diffSec = Math.round((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Whether the client has opened a proposal/invoice, shown right in the list so
 * "have they even looked at it?" is answerable before chasing them — the same
 * client_viewed events that fill the document's Activity feed, surfaced one
 * level up.
 *
 * Renders nothing for a document that hasn't been sent yet: an unopened draft
 * isn't news.
 */
export default function ClientViewedBadge({ firstViewedAt, lastViewedAt, viewCount, sent }) {
  if (!lastViewedAt) {
    if (!sent) return null;
    return (
      <span className="client-viewed client-viewed-none" title="No client has opened this yet">
        <EyeOff size={12} /> Not opened
      </span>
    );
  }

  const opens = Number(viewCount) || 1;
  const first = new Date(firstViewedAt || lastViewedAt);
  const tooltip = [
    `Last opened ${new Date(lastViewedAt).toLocaleString()}`,
    Number.isNaN(first.getTime()) ? '' : `First opened ${first.toLocaleString()}`,
    `${opens} ${opens === 1 ? 'open' : 'opens'}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <span className="client-viewed client-viewed-seen" title={tooltip}>
      <Eye size={12} /> Opened {formatRelativeTime(lastViewedAt)}
    </span>
  );
}

ClientViewedBadge.propTypes = {
  firstViewedAt: PropTypes.string,
  lastViewedAt: PropTypes.string,
  viewCount: PropTypes.number,
  sent: PropTypes.bool,
};
